import { Form } from "@unform/web";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
// useSearchParams só é exportado pelo react-router-dom nesta versão (v6).
import { useSearchParams } from "react-router-dom";

import AdminRoutes from "Routes/Components/Admin";

import { HTTPErrorCallback } from "Helpers/Error";
import { transformIntoArrayOptions } from "Helpers/Format";

import UseFile from "Hooks/UseFile";
import UseGlobalConfirm from "Hooks/UseGlobalConfirm";
import { UseGlobalLoading } from "Hooks/UseGlobalLoading";
import UseNotification from "Hooks/UseNotification";
import UseServices from "Hooks/UseServices";

import Button from "Components/Admin/Buttons";
import Input from "Components/Admin/Form/Input";
import Select from "Components/Admin/Form/Select";
import Section from "Components/Admin/Section";

import UpdateProjectCalendar from "../Calendar";
import ProjectConversations from "../Conversations";
import UpdateProjectCounterparts from "../Counterparts";
import UpdateProjectDocument from "../Document";
import UpdateProjectGallery from "../Gallery";
import UpdateProjectQuestions from "../Questions";
import UpdateProjectQuotas from "../Quotas";
import UpdateProjectReports from "../Reports";
import SponsorLinksTable from "../SponsorLinksTable";
import UpdateProjectSponsorOrders from "../SponsorOrders";
import SponsorDetails from "../Sponsors";
import * as S from "./styles";

// Opções de seletiva no campo Patrocinador usam value "selecao:<id>" para não
// colidir com os ids (numéricos) de usuários patrocinadores.
// "Aportado" em project_sponsor_statuses (ids fixos — src/config/
// projectSponsorStatuses.js no backend). Só vínculo aportado E com seletiva é
// oferecido no campo "Seletiva" dos formulários de Aporte/Contrapartida (D1).
const CONTRIBUTED_STATUS_ID = 8;

// Sub-abas aceitas no deep link `?tab=` (parte 4). Espelha os `value` de `tabs`
// mais abaixo; o backend usa os mesmos nomes em SECTION_TABS.
const TAB_VALUES = [
  "documents",
  "gallery",
  "counterparts",
  "quotas",
  "calendar",
  "conversations",
  "reports",
];

const isSeletivaValue = (value) =>
  typeof value === "string" && value.startsWith("selecao:");

// onProjectLoaded: publica o nome do projeto para o container das abas exibi-lo
// acima delas (E1). Fica aqui porque esta aba já busca o projeto.
const UpdateProjectDetails = ({ onProjectLoaded }) => {
  const navigate = useNavigate();

  const { id } = useParams();
  const { api } = UseServices();
  const { showNotification } = UseNotification();
  const { loading, setLoading } = UseGlobalLoading();
  const { uploadFile } = UseFile();
  const { addGlobalConfirm } = UseGlobalConfirm();

  const formRef = useRef(null);

  const downloadProjectPdf = async () => {
    setLoading(true);
    try {
      const response = await api.post("/projects/get-own-project-pdf", {
        project_id: id,
      });

      const { pdf_base64, filename } = response.data;

      const bytes = Uint8Array.from(atob(pdf_base64), (char) =>
        char.charCodeAt(0),
      );
      const blob = new Blob([bytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download =
        filename || `${selectedProject?.project_name || "projeto"}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      HTTPErrorCallback(error);
    } finally {
      setLoading(false);
    }
  };

  const [selectedProject, setSelectedProject] = useState({});

  const [sponsorOptions, setSponsorOptions] = useState([]);
  const [seletivaOptions, setSeletivaOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);

  const [selectedSponsors, setSelectedSponsors] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState(null);

  const [allDocs, setAllDocs] = useState({});

  /*
   * Deep link de sub-aba (D11 da parte 4): o e-mail ao admin e a tela de
   * pendências apontam para a SESSÃO que resolve o item, não para o topo do
   * projeto. Lista fechada, no precedente do lado comum — parâmetro
   * desconhecido cai no default e o comportamento anterior fica intacto.
   */
  const [searchParams] = useSearchParams();
  const deepTab = TAB_VALUES.includes(searchParams.get("tab"))
    ? searchParams.get("tab")
    : null;

  const [selectedTab, setSelectedTab] = useState(deepTab || "documents");

  /*
   * Quantas pendências há em cada sessão, chaveado pela própria sub-aba — a
   * chave vem pronta do backend, para o front não manter um segundo mapa
   * `kind → aba` que poderia divergir do usado no link dos e-mails.
   */
  const [pendingCounts, setPendingCounts] = useState({});

  const getPendingCounts = (project_id) => {
    api
      .post("/projects/pending-approvals/summary", { project_id })
      .then(({ data }) => setPendingCounts(data || {}))
      // Badge é acessório: se a contagem falhar, a tela abre sem ele em vez de
      // estourar um toast de erro sobre o detalhe do projeto.
      .catch(() => setPendingCounts({}));
  };

  // Opções do campo "Seletiva" de Aporte e Contrapartida (D1 da parte 4,
  // 05/09/2026): os vínculos DESTE projeto que estão em "Aportado" E têm
  // seletiva. O array já vem pronto do get-project-by-id — nenhuma requisição
  // nova. Antes o select recebia uma lista de USUÁRIOS filtrada pelos vínculos
  // diretos do projeto, conjunto que o modelo por seletiva esvaziou: o campo
  // vinha sempre vazio e criar aporte pela UI era impossível.
  const contributedLinkOptions = (selectedProject?.project_sponsor_links || [])
    .filter(
      (link) =>
        link.status_id === CONTRIBUTED_STATUS_ID && link.select_project_id,
    )
    .map((link) => ({
      value: link.project_sponsor_id,
      label: `${link.sponsor_name} - ${link.select_project_name}`,
    }));

  useEffect(() => {
    if (id) {
      return getProjectById(id);
    }

    // eslint-disable-next-line
  }, [id]);

  const getProjectById = (id) => {
    api
      .post("/projects/get-project-by-id", { project_id: id })
      .then((response) => {
        const project = response.data;

        setAllDocs(project?.project_files);
        setSelectedProject(project);

        // Nome exibido acima das abas pelo container (E1).
        if (onProjectLoaded) onProjectLoaded(project?.project_name || null);

        getSponsors();
        getActiveSeletivas(project?.project_select_projects);
        getSetup(project?.categories);
        getPendingCounts(id);
      })
      .catch((error) => {
        return HTTPErrorCallback(error);
      });
  };

  // Lista de usuários patrocinadores, usada apenas pelas sub-abas que resolvem
  // o nome do usuário-patrocinador (hoje só a aba oculta "Patrocinadores").
  //
  // ⚠️ Nem o campo "Seletivas" da aba Detalhes nem os formulários de Aporte e
  // Contrapartida usam esta lista. Até 05/09/2026 as duas abas recebiam
  // `linkedSponsorUsers` — os usuários com vínculo DIRETO no projeto —, e era
  // justamente por isso que o select vinha vazio: o modelo por seletiva não
  // cria vínculo direto. Agora elas recebem `contributedLinkOptions`.
  const getSponsors = () => {
    api
      .post("/users/list-users", { filters: { is_sponsor: "true" } })
      .then((response) => {
        const users = response.data?.users;
        const options = transformIntoArrayOptions(users, "id", "full_name");

        setSponsorOptions(options);
      })
      .catch((error) => {
        return HTTPErrorCallback(error);
      });
  };

  const getActiveSeletivas = (linked_ids = []) => {
    api
      .post("/select-projects/list-active", {})
      .then(({ data }) => {
        const options = (data || []).map((seletiva) => ({
          value: `selecao:${seletiva.id}`,
          label: seletiva.display_name,
        }));

        setSeletivaOptions(options);

        const preselected = (linked_ids || []).length
          ? options.filter((option) =>
              linked_ids.includes(Number(String(option.value).split(":")[1])),
            )
          : [];

        // O campo só tem seletivas: substitui a seleção inteira.
        setSelectedSponsors(preselected);
      })
      .catch((error) => {
        return HTTPErrorCallback(error);
      });
  };

  // O status deixou de ser selecionável (derivado do período — D14/D16): o
  // setup agora só alimenta as leis de incentivo.
  const getSetup = (category_ids) => {
    api
      .get("/projects/setup")
      .then((response) => {
        const categories = response.data?.categories;

        const category_options = transformIntoArrayOptions(
          categories,
          "id",
          "display_name",
        );

        setCategoryOptions(category_options);

        if (category_ids?.length) {
          const categories_found = category_options.filter((option) =>
            category_ids.includes(option.value),
          );
          setSelectedCategories(categories_found);
        }
      })
      .catch((error) => {
        return HTTPErrorCallback(error);
      });
  };

  const submit = async () => {
    setLoading(true);

    try {
      const files_to_send = [];

      const documents = allDocs ? Object.keys(allDocs) : [];

      for (const doc of documents) {
        const doc_files = allDocs[doc];

        for (const file of doc_files) {
          const image_location = file.file_src
            ? file.file_src
            : await uploadFile(file?.file);

          files_to_send.push({
            id: file?.id,
            name: file.name,
            sponsor_can_see: file.sponsor_can_see,
            type: "document",
            file_src: image_location,
            document_id: doc,
            project_id: selectedProject.id,
          });
        }
      }

      const sponsor_company_ids = (selectedSponsors || [])
        .filter((item) => !isSeletivaValue(item.value))
        .map((item) => item.value);

      const select_project_ids = (selectedSponsors || [])
        .filter((item) => isSeletivaValue(item.value))
        .map((item) => Number(String(item.value).split(":")[1]));

      await api.post("/projects/update-project-details", {
        project_id: selectedProject.id,
        sponsor_company_ids,
        select_project_ids,
        categories: selectedCategories?.map((category) => category.value),
        files: files_to_send,
      });

      setLoading(false);

      showNotification(
        "success",
        "Sucesso!",
        "Projeto atualizado com sucesso!",
      );

      setSelectedProject({});
      getProjectById(id);
    } catch (error) {
      setLoading(false);
      return HTTPErrorCallback(error);
    }
  };

  const duplicateProject = async () => {
    setLoading(true);

    try {
      await api.post("/projects/duplicate", {
        project_id: selectedProject.id,
      });

      setLoading(false);

      showNotification("success", "Sucesso!", "Projeto duplicado com sucesso!");
    } catch (error) {
      setLoading(false);
      return HTTPErrorCallback(error);
    }
  };

  const documentTab = () => {
    return (
      <>
        {selectedProject?.project_documents?.length ? (
          <>
            <S.Section>
              <span>Documentos</span>
              <hr />
            </S.Section>

            {selectedProject?.project_documents.map((file) => (
              <UpdateProjectDocument
                key={file?.id}
                file={file}
                defaultFiles={allDocs[file?.id]}
                setAllDocs={setAllDocs}
                loading={loading}
              />
            ))}

            <Button
              type="button"
              text="Atualizar"
              disabled={loading}
              onClick={() => formRef.current.submitForm()}
            />
          </>
        ) : null}
      </>
    );
  };

  const getComponent = () => {
    switch (selectedTab) {
      case "documents":
        return documentTab();
      /*
       * As 4 sessões que moderam recebem `onModerated`: aprovar um item precisa
       * derrubar o badge na hora. Sem isso o número só cairia na próxima visita
       * ao projeto, e o admin voltaria à sessão achando que ficou algo.
       */
      case "gallery":
        return (
          <UpdateProjectGallery
            projectId={selectedProject.id}
            onModerated={() => getPendingCounts(selectedProject.id)}
          />
        );
      case "counterparts":
        return (
          <UpdateProjectCounterparts
            projectId={selectedProject.id}
            linkOptions={contributedLinkOptions}
          />
        );
      case "quotas":
        return (
          <UpdateProjectQuotas
            projectId={selectedProject.id}
            linkOptions={contributedLinkOptions}
          />
        );
      case "questions":
        return <UpdateProjectQuestions projectId={selectedProject.id} />;
      case "calendar":
        return (
          <UpdateProjectCalendar
            projectId={selectedProject.id}
            onModerated={() => getPendingCounts(selectedProject.id)}
          />
        );
      case "conversations":
        return (
          <ProjectConversations
            projectId={selectedProject.id}
            onModerated={() => getPendingCounts(selectedProject.id)}
          />
        );
      case "reports":
        return (
          <UpdateProjectReports
            projectId={selectedProject.id}
            onModerated={() => getPendingCounts(selectedProject.id)}
          />
        );
      case "sponsor_orders":
        return (
          <UpdateProjectSponsorOrders
            projectId={selectedProject.id}
            sponsorOptions={sponsorOptions}
            getProjectById={getProjectById}
          />
        );
      case "sponsors":
        return (
          <SponsorDetails
            projectId={selectedProject.id}
            sponsorOptions={sponsorOptions}
            getProjectById={getProjectById}
          />
        );
      default:
        return <></>;
    }
  };

  // Abas OCULTAS a pedido do cliente (D28/Q15, 29/08/2026): "Perguntas"
  // ("voltaremos a exibir depois que for pedido"), "Validações" (as validações
  // acontecem nas respectivas abas) e "Patrocinadores" (removida; a tabela
  // "Patrocinadores vinculados" do corpo fica). Para reexibir, mova a entrada
  // de volta para `tabs` — os cases do switch continuam inteiros.
  // eslint-disable-next-line no-unused-vars
  const HIDDEN_TABS = [
    { text: "Validações", value: "sponsor_orders" },
    { text: "Patrocinadores", value: "sponsors" },
    { text: "Perguntas", value: "questions" },
  ];

  const tabs = [
    { text: "Documentos", value: "documents" },
    // "Galeria" → "Álbum" (D18 da parte 2, 03/09/2026).
    { text: "Álbum", value: "gallery" },
    { text: "Contrapartidas", value: "counterparts" },
    // "Plano de Cotas" → "Valor Aportado" (D5 da parte 2, 03/09/2026).
    { text: "Valor Aportado", value: "quotas" },
    // Renomeada de "Calendário" (D28).
    { text: "Cronograma", value: "calendar" },
    { text: "Mensagens", value: "conversations" },
    { text: "Relatórios", value: "reports" },
  ];

  const mainComponent = () => {
    return (
      <>
        <S.Buttons>
          <Button
            type="button"
            text="Voltar"
            disabled={loading}
            onClick={() => navigate(AdminRoutes.ListProjects.path)}
            width="30%"
          />

          <Button
            type="button"
            variant="critical"
            text="Duplicar Projeto"
            disabled={loading}
            onClick={(e) =>
              addGlobalConfirm({
                type: "critical",
                title: `Tem certeza que deseja duplicar este projeto?`,
                message:
                  "Essa ação não pode ser desfeita. Confirme para prosseguir.",
                confirmText: `Sim, duplicar`,
                denyText: "Não, desejo voltar",
                onClickConfirm: () => {
                  duplicateProject();
                },
                onClickDeny: () => {
                  e.stopPropagation();
                },
              })
            }
            width="40%"
          />
        </S.Buttons>
        <Form ref={formRef} onSubmit={submit}>
          <S.Section>
            <span>Detalhes</span>
            <hr />
          </S.Section>

          {/* Campo "Seletivas" (18/08/2026): antes era "Patrocinador" e misturava
              empresas patrocinadoras com seletivas. O cliente pediu apenas
              seletivas, ordenadas por patrocinador e depois pela seletiva (a
              ordem vem do backend).

              🛑 CONSEQUÊNCIA ACEITA PELO CLIENTE: como o campo não informa mais
              empresas, `sponsor_company_ids` vai sempre vazio no submit e o
              backend interpreta lista vazia como "remova os vínculos DIRETOS"
              (sem seletiva) deste projeto. É soft delete (`inactivated_at`),
              recuperável no banco, mas as contrapartidas e cotas penduradas
              nesses vínculos deixam de aparecer. Ver a decisão D6 em
              tasks_simples/ajustes_plataforma_18_08_plan.md. */}
          <Select
            name="sponsor_id"
            label="Seletivas"
            options={seletivaOptions}
            placeholder="Selecione as seletivas"
            value={selectedSponsors}
            onChange={setSelectedSponsors}
            maxWidth="100%"
            isMulti
            required
          />

          <SponsorLinksTable links={selectedProject?.project_sponsor_links} />

          {/* O status do projeto é DERIVADO do período de realização (Em
              captação → Em execução → Finalizado; "—" sem período) e deixou
              de ser editável — o valor vem calculado do backend
              (calculated_status). Ver tasks/projetos_aportador (D14/D16). */}
          <Input
            name="calculated_status"
            label="Status (derivado do período do projeto)"
            value={selectedProject?.calculated_status || "—"}
            disabled
            readOnly
          />

          <Select
            name="category"
            label="Lei de Incentivo"
            options={categoryOptions}
            placeholder="Selecione uma Lei de Incentivo"
            value={selectedCategories}
            onChange={setSelectedCategories}
            maxWidth="100%"
            isMulti
            required
          />

          <Button type="submit" text="Atualizar" disabled={loading} />
        </Form>

        <S.DownloadButton>
          <Button
            type="button"
            outline
            text="Baixar PDF do Projeto"
            onClick={downloadProjectPdf}
          />
        </S.DownloadButton>

        <S.Tabs>
          {tabs.map((tab) => (
            <S.TabSlot key={tab.value}>
              <Button
                type="button"
                text={tab.text}
                outline={selectedTab === tab.value}
                onClick={() => setSelectedTab(tab.value)}
              />

              {/* Badge de pendências (05/09/2026): o admin via as 7 sessões
                  iguais e precisava abrir uma a uma para achar o que avaliar. */}
              {pendingCounts[tab.value] ? (
                <S.TabBadge
                  title={`${pendingCounts[tab.value]} item(ns) aguardando aprovação`}
                >
                  {pendingCounts[tab.value]}
                </S.TabBadge>
              ) : null}
            </S.TabSlot>
          ))}
        </S.Tabs>
      </>
    );
  };

  return (
    <Section>
      <S.Wrapper>
        {mainComponent()}
        {getComponent()}
      </S.Wrapper>
    </Section>
  );
};

export default UpdateProjectDetails;
